import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import InventoryOutlined from "@mui/icons-material/InventoryOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { reassistButtonSx } from "@/features/reassistRequests/components/ReassistButton";
import {
  EmptyBlock,
  LoadFailed,
  LoadingCards,
} from "@/features/reassistRequests/components/StateBlocks";
import { costItemSx, TEXT } from "@/features/reassistRequests/utils/tokens";
import { formatDate } from "@/features/reassistRequests/utils/format";
import {
  PURCHASE_PAGE_SIZE,
  useSaleActions,
  useSalePurchases,
} from "../api/useReassistSale";
import { type SalePurchasedFile } from "../types";
import { startDownload } from "../utils/download";
import { SaleBuildingTypeChip } from "./SaleBuildingTypeChip";
import {
  displayName,
  formatBuildingFeatures,
  formatPrice,
} from "../utils/format";

/** 購入履歴の1行。物件名（建物の種類のタグと階数の補足つき）、資料の種類・ページ数・消費したクレジット・購入日、再ダウンロードの操作を出す。 */
const PurchasedRow: React.FC<{
  file: SalePurchasedFile;
  busy: boolean;
  onDownload: (file: SalePurchasedFile) => void;
}> = ({ file, busy, onDownload }) => {
  const meta = [
    file.documentType || "資料",
    file.pageCount !== null ? `${file.pageCount}ページ` : null,
    // 購入時に消費したクレジット。記録が無い（null）ときは出さない
    file.price !== null ? formatPrice(file.price) : null,
    `${formatDate(file.purchasedAt)}購入`,
  ]
    .filter((v): v is string => v !== null)
    .join("・");
  // 物件を見分けるための補足（階数）。建物の種類は物件名の隣のタグで出す
  const features = formatBuildingFeatures(file, ["floors"]);
  return (
    <Box
      component="li"
      sx={(theme) => ({
        ...costItemSx(theme),
        flexWrap: "wrap",
        columnGap: 2,
        rowGap: 1,
        alignItems: "center",
        listStyle: "none",
      })}
    >
      <Box sx={(theme) => ({ flex: `1 1 ${theme.spacing(24)}`, minWidth: 0 })}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: 1,
            rowGap: 0.5,
          }}
        >
          <Typography
            sx={{ ...TEXT.body, fontWeight: 700, overflowWrap: "anywhere" }}
          >
            {displayName(file)}
          </Typography>
          <SaleBuildingTypeChip buildingType={file.buildingType} />
        </Box>
        {features !== "" && (
          <Typography
            sx={{
              ...TEXT.meta,
              color: "text.secondary",
              overflowWrap: "anywhere",
            }}
          >
            {features}
          </Typography>
        )}
        <Typography
          sx={{
            ...TEXT.meta,
            mt: 0.5,
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {meta}
        </Typography>
      </Box>
      {file.isDownloadable ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadOutlined />}
          disabled={busy}
          onClick={() => {
            onDownload(file);
          }}
          aria-label={`${displayName(file)}の${
            file.documentType || "資料"
          }をダウンロード`}
          sx={(theme) => ({
            ...reassistButtonSx("outlined", "small")(theme),
            minWidth: theme.spacing(16),
          })}
        >
          ダウンロード
        </Button>
      ) : (
        <Typography sx={{ ...TEXT.meta, color: "text.secondary" }}>
          差し替えのため、ダウンロードできません
        </Typography>
      )}
    </Box>
  );
};

/**
 * 購入したリユース資料（黒塗り）の履歴。何を購入したかを、購入画面とは別に見られるようにする。
 * 本人と、同じ会社で共有が許可された人の購入が、購入日の新しい順に並ぶ。
 */
export const PurchasedDocumentsSection: React.FC = () => {
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(false);
  const { data, error, mutate } = useSalePurchases(offset);
  const { fetchDownload } = useSaleActions();
  const total = data?.count ?? 0;
  const hasPrev = offset > 0;
  const hasNext = offset + PURCHASE_PAGE_SIZE < total;

  const handleDownload = async (file: SalePurchasedFile): Promise<void> => {
    setBusy(true);
    try {
      const result = await fetchDownload(file.documentId, file.fileId);
      startDownload(result.url, result.fileName);
    } catch {
      toast.error(
        "ダウンロードできませんでした。少し待ってから、もう一度お試しください。"
      );
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode;
  if (error !== undefined && data === undefined) {
    body = (
      <LoadFailed
        message="購入した資料を読み込めませんでした。少し待ってから再読み込みしてください。"
        onRetry={() => {
          mutate().catch(() => undefined);
        }}
      />
    );
  } else if (data === undefined) {
    body = (
      <LoadingCards rows={2} height={{ xs: 88, [MOBILE_BREAKPOINT]: 72 }} />
    );
  } else if (data.count === 0) {
    body = (
      <EmptyBlock
        icon={InventoryOutlined}
        title="購入した役所資料はまだありません"
        description="「役所資料を探す」から、リユース資料（黒塗り）を購入できます。"
      />
    );
  } else {
    body = (
      <>
        <Stack
          component="ul"
          spacing={1}
          aria-label="購入した役所資料"
          sx={{ m: 0, p: 0 }}
        >
          {data.results.map((file) => (
            <PurchasedRow
              key={file.fileId}
              file={file}
              busy={busy}
              onDownload={(f) => {
                void handleDownload(f);
              }}
            />
          ))}
        </Stack>
        {(hasPrev || hasNext) && (
          <Stack
            direction="row"
            sx={{
              mt: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Button
              size="small"
              disabled={!hasPrev}
              onClick={() => {
                setOffset(Math.max(0, offset - PURCHASE_PAGE_SIZE));
              }}
            >
              前の{PURCHASE_PAGE_SIZE}件
            </Button>
            <Typography sx={{ ...TEXT.meta, color: "text.secondary" }}>
              {offset + 1}〜{Math.min(offset + PURCHASE_PAGE_SIZE, total)}件 /
              全{total}件
            </Typography>
            <Button
              size="small"
              disabled={!hasNext}
              onClick={() => {
                setOffset(offset + PURCHASE_PAGE_SIZE);
              }}
            >
              次の{PURCHASE_PAGE_SIZE}件
            </Button>
          </Stack>
        )}
      </>
    );
  }
  return <>{body}</>;
};
